// Shared IANA timezone parsing + local-zone detection.
//
// Deliberately dependency-free (no dayjs, no models) so it can be imported from
// server code, client code, and pure utils alike. `streakMath.ts` and
// `relativeTimestamp.ts` both build on this so there is one definition of "is
// this a usable timezone" and one fallback zone across the app.

// The canonical fallback when we don't know the viewer's zone. Matches
// DIGEST_TIMEZONE / ACTIVE_DAY_TIMEZONE and the streaks fallback.
export const FALLBACK_TIMEZONE = "America/New_York";

/**
 * Validate an IANA timezone name, returning null for anything missing or
 * unusable. Validated by actually constructing a formatter (accepts aliases
 * like Asia/Calcutta that `Intl.supportedValuesOf` omits) so an unknown name
 * can never reach `dayjs.tz` or Postgres `AT TIME ZONE`.
 *
 * Returns null rather than the fallback so callers that only want to *record* a
 * timezone can tell "we weren't told" from "the viewer is in New York".
 */
export function parseTimezone(tz: unknown): string | null {
  if (typeof tz !== "string" || !tz.trim()) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

/** Resolve a timezone, falling back to Eastern for anything missing or invalid. */
export function resolveTimezone(tz: unknown): string {
  return parseTimezone(tz) ?? FALLBACK_TIMEZONE;
}

/**
 * The zone this environment is actually in, or null if it can't be determined.
 * On the server this is the *server's* zone (UTC in prod) — which is exactly why
 * SSR must not use it for viewer-facing day math; see `relativeTimestamp.ts`.
 */
export function localTimezone(): string | null {
  try {
    return parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}
